import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import InputMask from 'react-input-mask';
import { useHistory } from 'react-router-dom';
import { clientApi } from 'helpers/api/client';
import { useDebounce } from 'hooks/useDebounce';

import cn from 'classnames';
import { Icon } from 'components/Icon';

import '../style.scss';

const TableProp = ({ title, icon }) => {
  return (
    <div className="requisites-prop">
      {title}
      <Icon name={icon} style={{ display: 'inline-block', margin: '0 5px' }} />
      {'  -  '}
    </div>
  );
};

TableProp.propTypes = {
  title: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
};

const TableValueDynamic = ({
  value,
  onChange,
  canEdit,
  isMasked,
  mask,
  placeholder,
  className,
  onFocus,
  onBlur,
  style,
}) => {
  const handleChange = ({ target: { value } }) => {
    onChange(value);
  };

  let input;

  if (isMasked) {
    input = (
      <InputMask
        formatChars={{
          9: '[0-9]',
          A: '[А-Я]',
        }}
        maskChar="X"
        mask={mask}
        value={value}
        onChange={handleChange}
        onFocus={onFocus}
        onBlur={onBlur}
        style={style}
      >
        {(inputProps) => (
          <input {...inputProps} className={`${className} requisites-value`} placeholder={placeholder} style={style} />
        )}
      </InputMask>
    );
  } else {
    input = (
      <input
        className={`${className} requisites-value`}
        value={value}
        onFocus={onFocus}
        onBlur={onBlur}
        onChange={handleChange}
        placeholder={placeholder}
      />
    );
  }

  if (canEdit) {
    return input;
  }

  return <div className="requisites-value">{value}</div>;
};

TableValueDynamic.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  canEdit: PropTypes.bool,
  mask: PropTypes.string,
  placeholder: PropTypes.string,
  isMasked: PropTypes.bool,
  className: PropTypes.string,
  style: PropTypes.object,
};

TableValueDynamic.defaultProps = {
  value: '',
  onChange: () => {},
  canEdit: false,
  style: {},
};

const TableValueDynamicAutoComplete = ({
  err,
  value,
  onChange,
  mask,
  canEdit,
  placeholder,
  styles,
  isMasked,
  historyParams,
  search,
}) => {
  const debounceValue = useDebounce(value, 100, true);
  const history = useHistory();

  const [result, setResult] = useState([]);
  const [focus, setFocus] = useState(false);

  const isOpen = canEdit && focus;

  useEffect(() => {
    if (debounceValue && !debounceValue.includes('X') && canEdit && focus) {
      getClients(debounceValue);
    } else {
      setResult([]);
    }
  }, [debounceValue, canEdit, focus]);

  const handleClient = (client) => {
    history.push(`/client/${client.id}`, historyParams);
  };

  const getClients = async (value) => {
    const filters = {};
    filters[search] = value;
    const { data } = await clientApi.getClientList(filters);
    setResult(data);
  };

  const onFocus = () => {
    setFocus(true);
  };

  const onBlur = () => {
    setTimeout(() => {
      setFocus(false);
    }, 200);
  };

  const style = { ...styles, borderRadius: result.length > 0 ? '8px 8px 0 0' : '8px' };
  return (
    <div className={cn(canEdit && 'is-open', 'autocomplete-input')}>
      <TableValueDynamic
        err={err}
        canEdit={canEdit}
        value={value}
        onChange={onChange}
        mask={mask}
        placeholder={placeholder}
        className={cn(isOpen && 'is-open')}
        isMasked={isMasked}
        onFocus={onFocus}
        onBlur={onBlur}
        styles={style}
      />
      {!!result.length && focus && canEdit && (
        <div className="autocomplete-options scrollable">
          {result.map((elem) => {
            return (
              <div key={elem.id} className="autocomplete-item">
                <div className="contragent-select">
                  <Icon
                    name={
                      elem.isPendingDeletion
                        ? 'userverify-pending-deletition'
                        : elem.isTemporary
                        ? 'userverify-yellow'
                        : 'userverify'
                    }
                  />
                  <div className="contragent-data"> {elem.FIO}</div>
                </div>
                <div
                  className="button-case"
                  onClick={() => handleClient(elem)}
                  data-tip
                  data-for="client-profile-tooltip"
                >
                  <Icon name="usercard" />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

TableValueDynamicAutoComplete.propTypes = {
  err: PropTypes.bool,
  value: PropTypes.string.isRequired,
  search: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  mask: PropTypes.string,
  placeholder: PropTypes.string,
  canEdit: PropTypes.bool,
  isMasked: PropTypes.bool,
  historyParams: PropTypes.object,
};

TableValueDynamicAutoComplete.defaultProps = {
  err: false,
  autoFocus: false,
  onChange: () => {},
  historyParams: {},
};

const TableLabel = ({ show, icon, text, onClick, rounded }) => {
  if (!show) {
    return <></>;
  }

  return (
    <>
      <div></div>
      <div
        className={`requisites-label ${onClick ? 'is-active' : ''} ${rounded ? 'is-rounded' : ''}`}
        onClick={onClick}
      >
        {icon && <Icon name={icon} style={{ display: 'inline-block' }} />}
        {'  '}
        {text}
      </div>
    </>
  );
};

TableLabel.propTypes = {
  show: PropTypes.bool,
  icon: PropTypes.string,
  text: PropTypes.string.isRequired,
  onClick: PropTypes.func,
  rounded: PropTypes.bool,
};

TableLabel.defaultProps = {
  show: true,
  icon: null,
  onClick: () => {},
  rounded: false,
};

export { TableProp, TableValueDynamic, TableLabel, TableValueDynamicAutoComplete };
